import React, { useState, useCallback } from "react";
import { DateTime } from "luxon";
import DateTimePicker from "../Shared/DateTimePicker/DateTimePicker";

const CustomTimeRequestStep = ({
  selectedDate,
  onDateChange,
  selectedTime,
  onTimeChange,
  primaryColor = "blue-600",
  themeMode = "light",
  businessName,
  onBackToTimeSlots,
  showBackButton = false,
}) => {
  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    if (selectedDate && selectedTime) {
      // Combine existing date and time if both exist
      const [hours, minutes] = selectedTime.split(":");
      const datetime = new Date(selectedDate);
      datetime.setHours(parseInt(hours), parseInt(minutes));
      return datetime;
    } else if (selectedDate) {
      // Use selected date with current time
      return selectedDate;
    }
    return new Date();
  });

  const handleDateTimeChange = useCallback(
    (dateWithTimezone) => {
      const { date } = dateWithTimezone;
      setSelectedDateTime(date);
      onDateChange(date); // Date already contains full datetime with correct time
      // Convert time to HH:MM format for UI display
      if (date) {
        const luxonDate = DateTime.fromJSDate(date);
        const timeString = luxonDate.toFormat("HH:mm");
        onTimeChange(timeString);
      }
    },
    [onDateChange, onTimeChange]
  );

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {showBackButton && (
        <div>
          <button
            type="button"
            onClick={onBackToTimeSlots}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-themed-primary bg-themed-card border border-themed rounded-pill hover:bg-opacity-80 focus:outline-none focus-themed transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Available Times
          </button>
        </div>
      )}

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-themed-primary mb-2">
          Request custom appointment time
        </h3>
        <p className="text-sm text-themed-secondary">
          {showBackButton
            ? `There are no standard openings here. Choose your preferred time and we'll let you know if it works.`
            : `This location is outside our usual area. Choose your preferred date and time — we'll review it and get back to you.`}
        </p>
      </div>

      {/* Date and Time Selection */}
      <div className="bg-themed-card rounded-lg border border-themed p-6 space-y-6">
        {/* Date & Time Picker */}
        <div>
          <DateTimePicker
            label="Preferred Date & Time *"
            onChange={handleDateTimeChange}
            defaultSelected={selectedDateTime}
            is12HourClock={true}
            primaryColor={primaryColor}
            themeMode={themeMode}
          />
        </div>

        {/* Selected Summary */}
        {selectedDateTime && (
          <div className="pt-4 border-t border-themed">
            <div className="p-3 bg-themed-card-elevated rounded-lg">
              <p className="text-sm font-medium text-themed-primary">
                Request Summary:
              </p>
              <p className="text-sm text-themed-secondary mt-1">
                {DateTime.fromJSDate(selectedDateTime).toFormat("cccc, MMMM d, yyyy 'at' h:mm a")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTimeRequestStep;
